import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { formatNOK } from "@/lib/format";
import {
  convertOfferLineUnit,
  effectiveUnitPrice,
  hourlyBasisFromUnitPrice,
  deriveLineAmount,
  isOfferTimeUnit,
  normalizeOfferUnit,
  overtimeUnitOf,
  patchFromOvertimeSplit,
  readOvertimeSplit,
  suggestOvertimeSplit,
  type OvertimeSplit,
} from "@/lib/offer-calc";
import { PHASE_COLORS } from "./offer-phase-tokens";
import { OfferUnitCell } from "./OfferUnitCell";
import { OfferOvertimeRow } from "./OfferOvertimeRow";
import {
  handleOfferArrowNav,
  isOvertimeHotkey,
  OFFER_LINE_GRID,
  unitFromHotkey,
} from "./offer-line-nav";
import { SrIcon } from "@/components/ui/SrIcon";
import type { OfferLine } from "@/hooks/use-offers";

interface Props {
  line: OfferLine;
  hourlyRate: number;
  /** Striped rad (annenhver) for visuell luft. */
  striped?: boolean;
  /** Avgir Tab/Enter på siste celle — siste rad-kontrakt. */
  onTabOut?: () => void;
  /** Drag-handlers fra parent (sortering). */
  dragHandlers?: React.HTMLAttributes<HTMLDivElement>;
  isDragging?: boolean;
  /** Drop-mål under dra: vis innsettingslinje over denne raden. */
  isDropTarget?: boolean;
  /** Option-dra: drop-linjen er en kopi, ikke flytt. */
  isCopyDrop?: boolean;
  /** Hvis true, fokuser mengde-feltet på første render (ny linje). */
  autoFocus?: boolean;
  /** VIB-950: overtidssplit er tilbud-spesifikt. Mersalg setter false for å
   *  skjule overtid-inngangene (mersalg-linjer har aldri split). Default true. */
  allowOvertime?: boolean;
  /** VIB-950: låser alle inputs (godkjent mersalg vises read-only). Default false. */
  readOnly?: boolean;
  /** Katalogens default overtidssats (kr/t) for malen på linja (VIB-2087). */
  defaultOvertimeRate?: number;
  onChange: (patch: Partial<OfferLine>) => void;
  onDelete: () => void;
  onDuplicate?: () => void;
}

/**
 * Rad i Variant 4-design: drag-handle og fasestripe i venstre kant,
 * inline-editerbar beskrivelse, mengde, enhet, sats, innkjøp, beløp.
 *
 * Logikk-detaljer som beholdes fra forrige versjon:
 * - Internal hourly t↔d-konvertering oppskalerer/nedskalerer unit_price (×8 / ÷8)
 *   slik at amount per tid-enhet bevares.
 * - Innkjøps-feltet er låst for ikke-eksterne linjer.
 * - amount = deriveLineAmount(...) — ikke håndberegnet her.
 */
export function OfferLineRow({
  line,
  hourlyRate,
  striped,
  onTabOut,
  dragHandlers,
  isDragging,
  isDropTarget,
  isCopyDrop,
  autoFocus,
  allowOvertime = true,
  readOnly = false,
  defaultOvertimeRate = 0,
  onChange,
  onDelete,
  onDuplicate,
}: Props) {
  const quantityRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && quantityRef.current) {
      quantityRef.current.focus();
      quantityRef.current.select();
    }
  }, [autoFocus]);
  const isInternalHourly =
    line.cost_type === "internal" && line.pricing_type === "hourly";
  const isExternal = line.cost_type === "external";
  // Auto-genererte linjer (f.eks. tilbudsskriving) låses fra manuell
  // redigering — verdien styres av systemet basert på BF.
  const isLocked = !!line.is_auto_generated || readOnly;
  const lockedTitle = line.is_auto_generated
    ? "Auto-beregnet (2 t per 100 000 i GEEK). Låses ved aksept."
    : undefined;
  // Overtidssplit (VIB-663f). Sub-raden er kollapsibel — OT-badgen er
  // indikator på persistert overtid og fungerer som åpne/lukke-toggle.
  // Klokke-knappen åpner sub-raden første gang (når linja ikke har split).
  // readOvertimeSplit filtrerer oq<=0, så vi må holde åpne-tilstanden
  // utenfor splitten selv — ellers ville en tom draft kappes umiddelbart.
  const activeSplit = readOvertimeSplit(line);
  const hasPersistedSplit = !!activeSplit;
  const [overtimeOpen, setOvertimeOpen] = useState(false);
  // displaySplit er det OfferOvertimeRow får — persistert split hvis den
  // finnes, ellers en tom draft for utfylling.
  const lineTimeUnit: "t" | "d" =
    normalizeOfferUnit(line.unit ?? "t") === "d" ? "d" : "t";
  const displaySplit: OvertimeSplit = activeSplit ??
    suggestOvertimeSplit(defaultOvertimeRate, lineTimeUnit);

  const [description, setDescription] = useState(line.description ?? "");
  const [quantity, setQuantity] = useState(String(line.quantity ?? 0));
  const [unitPrice, setUnitPrice] = useState(String(line.unit_price ?? 0));
  const [unit, setUnit] = useState(normalizeOfferUnit(line.unit ?? "stk"));
  const [purchasePrice, setPurchasePrice] = useState(String(line.purchase_price ?? 0));
  /** Lokal beløpsvisning mens parent/refetch henger etter unit-toggle. */
  const [displayAmount, setDisplayAmount] = useState(line.amount ?? 0);

  useEffect(() => setDescription(line.description ?? ""), [line.description]);
  useEffect(() => setQuantity(String(line.quantity ?? 0)), [line.quantity]);
  useEffect(() => setUnitPrice(String(line.unit_price ?? 0)), [line.unit_price]);
  useEffect(() => setUnit(normalizeOfferUnit(line.unit ?? "stk")), [line.unit]);
  useEffect(() => setPurchasePrice(String(line.purchase_price ?? 0)), [line.purchase_price]);
  useEffect(() => setDisplayAmount(line.amount ?? 0), [line.amount]);

  function commitDescription() {
    const trimmed = description.trim();
    if (!trimmed) {
      setDescription(line.description ?? "");
      return;
    }
    if (trimmed !== (line.description ?? "")) {
      onChange({ description: trimmed });
    }
  }

  function recompute(patch: Partial<OfferLine>) {
    // Bevar evt. aktivt overtidstillegg når qty/unit/sats endres — det
    // legges på som tillegg over hovedlinjens nye verdier.
    const next = {
      cost_type: line.cost_type,
      pricing_type: line.pricing_type,
      quantity: patch.quantity ?? line.quantity ?? 0,
      unit: patch.unit ?? line.unit ?? "stk",
      unit_price: patch.unit_price ?? line.unit_price ?? 0,
      overtime_split: line.overtime_split ?? null,
    };
    const amount = deriveLineAmount(next, hourlyRate);
    setDisplayAmount(amount);
    // VIB-2097: ny sats på t/d oppdaterer tidsbasisen; på fast/stk rører vi
    // den ikke (klumpsummen sier ingenting om timesatsen).
    const basisPatch =
      patch.unit_price !== undefined && hourlyBasisFromUnitPrice(next.unit, next.unit_price) != null
        ? { rate_basis_hourly: hourlyBasisFromUnitPrice(next.unit, next.unit_price) }
        : {};
    onChange({ ...patch, ...basisPatch, amount });
  }

  function commitQuantity() {
    recompute({ quantity: Number(quantity) || 0 });
  }

  function commitUnitPrice() {
    recompute({ unit_price: Number(unitPrice) || 0 });
  }

  function commitUnit(nextUnitRaw: string) {
    const nextUnit = normalizeOfferUnit(nextUnitRaw);
    // Lokal `unit`-state (ikke kun line.unit) — props henger etter mutate,
    // så raske klikk ellers ganger ×8 flere ganger mot stale "t".
    if (nextUnit === unit) return;

    // Interne linjer: t↔d konverterer sats (×8/÷8), til fast beholder sats
    // og beløp, fra fast hentes lagret tidsbasis eller katalogens timesats
    // (VIB-2097, se convertOfferLineUnit). Gjelder uavhengig av
    // pricing_type. Ellers endres kun enhetslabel.
    if (
      line.cost_type === "internal" &&
      isOfferTimeUnit(unit) &&
      isOfferTimeUnit(nextUnit)
    ) {
      const price = effectiveUnitPrice(
        unit,
        Number(unitPrice) || line.unit_price || 0,
        hourlyRate,
      );
      const converted = convertOfferLineUnit({
        fromUnit: unit,
        toUnit: nextUnit,
        quantity: Number(quantity) || 0,
        unitPrice: price,
        // Tidsbasis når linjen forlater fast/stk (VIB-2097): lagret basis fra
        // sist den sto på t/d, ellers katalogens timesats (prop hourlyRate).
        hourlyBasis: line.rate_basis_hourly ?? hourlyRate,
      });
      // fast har ingen tidsbasis → fixed; t/d → hourly (VIB-2097).
      const pricingType = converted.unit === "fast" ? "fixed" : "hourly";
      const amount = deriveLineAmount(
        {
          cost_type: line.cost_type,
          pricing_type: pricingType,
          quantity: Number(quantity) || 0,
          unit: converted.unit,
          unit_price: converted.unit_price,
          overtime_split: line.overtime_split ?? null,
        },
        hourlyRate,
      );
      setUnit(converted.unit);
      setUnitPrice(String(converted.unit_price));
      setDisplayAmount(amount);
      onChange({
        unit: converted.unit,
        unit_price: converted.unit_price,
        amount,
        pricing_type: pricingType,
        rate_basis_hourly: converted.rate_basis_hourly,
      });
      return;
    }
    setUnit(nextUnit);
    recompute({ unit: nextUnit });
  }

  function commitPurchasePrice() {
    commitPurchaseValue(purchasePrice);
  }

  function onLastCellKey(e: React.KeyboardEvent) {
    if (e.key === "Tab" && !e.shiftKey && onTabOut) {
      e.preventDefault();
      onTabOut();
    } else if (e.key === "Enter" && onTabOut) {
      e.preventDefault();
      onTabOut();
    }
  }

  const allowedUnits =
    line.cost_type === "internal"
      ? (["t", "d", "fast"] as const)
      : (["t", "d", "stk", "fast"] as const);

  // t/d/s/f bytter enhet uten å forlate feltet (antall/sats/innkjøp).
  function tryUnitHotkey(e: React.KeyboardEvent): boolean {
    if (isLocked || e.altKey || e.metaKey || e.ctrlKey) return false;
    const next = unitFromHotkey(e.key, allowedUnits);
    if (!next) return false;
    e.preventDefault();
    commitUnit(next);
    return true;
  }

  const canOpenOvertime = allowOvertime && isInternalHourly && !isLocked;

  // o åpner overtidslinjen uten å persistere og uten å flytte fokus.
  function tryOvertimeHotkey(e: React.KeyboardEvent): boolean {
    if (!canOpenOvertime || e.altKey || e.metaKey || e.ctrlKey) return false;
    if (!isOvertimeHotkey(e.key)) return false;
    e.preventDefault();
    enableOvertime();
    return true;
  }

  // VIB-1000: ↑/↓ endrer aldri tallet. De hopper til samme felt på nablinja.
  // Esc → reset local state før blur, så onBlur ikke skriver noe nytt.
  function handleQtyKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (handleOfferArrowNav(e)) return;
    if (tryUnitHotkey(e)) return;
    if (tryOvertimeHotkey(e)) return;
    if (e.key === "Escape") {
      setQuantity(String(line.quantity ?? 0));
      e.currentTarget.blur();
    }
  }

  function handleNumericKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (handleOfferArrowNav(e)) return;
    if (tryUnitHotkey(e)) return;
    tryOvertimeHotkey(e);
  }

  // Marginvisning er borte fra inline-raden i Variant 4 — den vises i
  // separat info-cell ved hover for å holde linjen kompakt.
  const phaseColor = PHASE_COLORS[line.phase];

  function commitPurchaseValue(raw: string) {
    const num = Number(raw) || 0;
    // Skriv et tall i innkjøp-feltet → linjen blir automatisk ekstern.
    if (!isExternal && num > 0) {
      onChange({ cost_type: "external", purchase_price: num });
    } else {
      onChange({ purchase_price: num });
    }
  }

  function enableOvertime() {
    // Åpne sub-raden visuelt uten å skrive split til DB. PL fyller inn
    // verdier; først ved overtime_qty > 0 persisteres splitten.
    setOvertimeOpen(true);
  }

  function toggleOvertimePanel() {
    // OT-badgen kalles for å vise/skjule sub-raden uten å røre persistert
    // split. Lukking her er bare visuell — splitten ligger i line.overtime_split.
    setOvertimeOpen((prev) => !prev);
  }

  function applySplit(next: OvertimeSplit) {
    onChange(
      patchFromOvertimeSplit(
        next,
        Number(line.quantity) || 0,
        Number(line.unit_price) || 0,
      ),
    );
  }

  function clearOvertime() {
    // Fjern både sub-raden og evt. persistert split.
    setOvertimeOpen(false);
    if (hasPersistedSplit) {
      const baseQty = Number(line.quantity) || 0;
      const basePrice = Number(line.unit_price) || 0;
      onChange({
        overtime_split: null,
        amount: Math.round(baseQty * basePrice),
      });
    }
  }

  return (
    <div
      data-line-id={line.id}
      className={cn(overtimeOpen && "border-b border-border/40 last:border-0")}
    >
    <div
      {...dragHandlers}
      tabIndex={-1}
      className={cn(
        "grid items-center min-h-[28px] group relative",
        // Når overtid-panelet er åpent tegner vi border på wrapperen istedenfor raden
        !overtimeOpen && "border-b border-border/40 last:border-0",
        OFFER_LINE_GRID,
        striped && "bg-muted/20",
        isDragging && "opacity-50",
        isDropTarget && "bg-primary/10",
        isExternal && "italic",
      )}
    >
      {isDropTarget && (
        <span
          data-drop-indicator
          data-drop-copy={isCopyDrop ? "true" : undefined}
          className="pointer-events-none absolute inset-x-0 -top-px z-10 flex items-center"
          aria-hidden
        >
          <span
            className={cn(
              "w-1.5 h-1.5 rounded-full shrink-0",
              isCopyDrop ? "bg-amber-500" : "bg-primary",
            )}
          />
          <span
            className={cn(
              "flex-1 h-0.5",
              isCopyDrop ? "bg-amber-500" : "bg-primary",
            )}
          />
        </span>
      )}
      {/* Drag-handle + fase-stripe */}
      <div
        className="h-full w-full flex items-center justify-center cursor-grab relative select-none"
        title="Dra for å flytte linjen"
      >
        <span
          className="absolute left-0 top-1 bottom-1 w-[3px] rounded-sm"
          style={{ background: phaseColor }}
          aria-hidden
        />
        <span
          className="text-[10px] text-muted-foreground/60 leading-none opacity-0 group-hover:opacity-100 transition-opacity"
          aria-hidden
        >
          ⋮⋮
        </span>
      </div>

      {/* Beskrivelse */}
      <div className="pl-1 pr-2 flex items-center gap-1.5">
        {isLocked && (
          <SrIcon
            name="lock"
            className="w-3 h-3 text-muted-foreground/60 shrink-0"
            aria-hidden
          />
        )}
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={commitDescription}
          readOnly={isLocked}
          title={lockedTitle}
          onKeyDown={(e) => {
            if (handleOfferArrowNav(e)) return;
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          data-offer-nav="description"
          className={cn(
            "w-full h-6 px-1 text-sm font-medium bg-transparent rounded-sm",
            "border border-transparent focus:bg-background focus:border-border focus:outline-none",
            isLocked && "text-muted-foreground italic cursor-not-allowed",
          )}
          aria-label="Beskrivelse"
        />
        {line.source_idea_card_id && (
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground shrink-0" title="Laget fra et kort på Idé-tavlen">
            Fra idé
          </span>
        )}
        {/* Klokke-knapp: åpner sub-raden første gang når linja ikke har
            persistert overtid. Når overtid er persistert overtas OT-badgen
            som toggle, og klokken skjules for å unngå dobbel inngang. */}
        {allowOvertime && isInternalHourly && !isLocked && !hasPersistedSplit && !overtimeOpen && (
          <button
            type="button"
            onClick={enableOvertime}
            tabIndex={-1}
            className={cn(
              "w-6 h-6 flex items-center justify-center rounded-sm shrink-0",
              "text-muted-foreground/50 hover:text-amber-600 hover:bg-amber-50",
              "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100 transition-all",
            )}
            title="Splitt i normal + overtid"
            aria-label="Vis overtid"
          >
            <SrIcon name="clock" className="w-3 h-3" />
          </button>
        )}
        {/* OT-badge: vises når linja har persistert overtid. Klikkbar
            for å åpne/lukke sub-raden uten å røre persistert split. */}
        {allowOvertime && hasPersistedSplit && (
          <button
            type="button"
            onClick={toggleOvertimePanel}
            tabIndex={-1}
            className={cn(
              "text-[10px] font-medium uppercase tracking-wider shrink-0",
              "text-amber-700 dark:text-amber-400 hover:text-amber-600",
              "cursor-pointer rounded-sm px-1 -mx-1",
              "focus:outline-none focus:ring-1 focus:ring-amber-400/40",
            )}
            title={overtimeOpen ? "Skjul overtidstillegg" : "Vis overtidstillegg"}
            aria-label={overtimeOpen ? "Skjul overtidstillegg" : "Vis overtidstillegg"}
            aria-expanded={overtimeOpen}
          >
            ot
          </button>
        )}
      </div>

      {/* Mengde — total antall enheter. Overtid er bare et tillegg
          på et delsegment, så denne forblir redigerbar også når
          overtidstillegg er aktivt. */}
      <input
        ref={quantityRef}
        type="number"
        step="0.25"
        min={0}
        value={quantity}
        placeholder="—"
        readOnly={isLocked}
        title={lockedTitle}
        data-cell="quantity"
        data-offer-nav="quantity"
        onChange={(e) => setQuantity(e.target.value)}
        onBlur={commitQuantity}
        onKeyDown={handleQtyKeyDown}
        className={cn(
          "h-6 w-full px-1 text-xs text-right tabular-nums bg-transparent rounded-sm",
          "border border-transparent focus:bg-background focus:border-border focus:outline-none",
          isLocked && "text-muted-foreground cursor-not-allowed",
        )}
        aria-label="Mengde"
      />

      {/* Enhet */}
      <div className="px-1">
        <OfferUnitCell
          value={unit}
          allowedUnits={allowedUnits}
          disabled={isLocked}
          navCol="unit"
          onChange={commitUnit}
          onOvertimeHotkey={canOpenOvertime ? enableOvertime : undefined}
        />
      </div>

      {/* Sats — normal-sats per enhet. Overtid har sin egen tilleggssats
          i sub-raden, så denne forblir redigerbar. */}
      <input
        type="number"
        min={0}
        value={unitPrice}
        placeholder="—"
        readOnly={isLocked}
        title={lockedTitle}
        onChange={(e) => setUnitPrice(e.target.value)}
        onBlur={commitUnitPrice}
        data-offer-nav="rate"
        onKeyDown={handleNumericKeyDown}
        onWheel={(e) => e.currentTarget.blur()}
        className={cn(
          "h-6 w-full px-1 text-xs text-right tabular-nums bg-transparent rounded-sm",
          "border border-transparent focus:bg-background focus:border-border focus:outline-none",
          isLocked && "text-muted-foreground cursor-not-allowed",
        )}
        aria-label="Sats"
      />

      {/* Innkjøp — alltid skrivbar. Skriv et tall for å markere linjen som
          ekstern. "+"-placeholder antyder dette for ikke-eksterne linjer. */}
      <input
        type="number"
        min={0}
        value={isExternal ? purchasePrice : purchasePrice && purchasePrice !== "0" ? purchasePrice : ""}
        placeholder={isExternal ? "—" : "+"}
        readOnly={isLocked}
        onChange={(e) => setPurchasePrice(e.target.value)}
        onBlur={commitPurchasePrice}
        onWheel={(e) => e.currentTarget.blur()}
        data-offer-nav="purchase"
        onKeyDown={(e) => {
          handleNumericKeyDown(e);
          if (e.defaultPrevented) return;
          onLastCellKey(e);
        }}
        className={cn(
          "h-6 w-full px-1 text-xs text-right tabular-nums bg-transparent rounded-sm",
          "border border-transparent focus:bg-background focus:border-border focus:outline-none",
          !isExternal && "text-muted-foreground/40",
          !isExternal && "placeholder:text-muted-foreground/60 placeholder:text-base placeholder:font-normal",
          isLocked && "text-muted-foreground cursor-not-allowed",
        )}
        title={isLocked ? lockedTitle : isExternal ? "Innkjøpspris" : "Skriv et tall for å gjøre linjen ekstern"}
        aria-label="Innkjøpspris"
      />

      {/* Beløp */}
      <span
        className={cn(
          "text-right tabular-nums font-semibold text-sm pr-2 pl-1",
          displayAmount === 0 && "text-muted-foreground/70 font-normal",
        )}
      >
        {formatNOK(displayAmount)}
      </span>

      {/* Dupliser + slett (skjult for auto-genererte linjer) */}
      {!isLocked && (
        <div className="flex items-center justify-end">
          {onDuplicate && (
            <button
              type="button"
              onClick={onDuplicate}
              tabIndex={-1}
              className={cn(
                "w-5 h-6 flex items-center justify-center text-muted-foreground/50",
                "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100 hover:text-foreground transition-all",
              )}
              title="Dupliser linje (⌘D)"
              aria-label="Dupliser linje"
            >
              <SrIcon name="copy" className="w-3 h-3" />
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            className={cn(
              "w-5 h-6 flex items-center justify-center text-muted-foreground/50",
              "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100 hover:text-destructive transition-all",
            )}
            aria-label={`Slett ${line.description}`}
          >
            <SrIcon name="x" className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>

    {allowOvertime && overtimeOpen && (
      <OfferOvertimeRow
        split={displaySplit}
        unit={overtimeUnitOf(displaySplit, line.unit)}
        onChange={applySplit}
        onClear={clearOvertime}
      />
    )}
    </div>
  );
}
